import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { supabase } from '../supabaseClient';

const LoginWrapper = styled.div`
  width: 100%;
  max-width: 420px;
`;

const LoginCard = styled(motion.div)`
  background: ${props => props.theme.colors.surfaceCard};
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  padding: ${props => props.theme.spacing.xxl};
  border-radius: 24px;
  box-shadow: ${props => props.theme.shadows.large};
  text-align: center;
`;

const LogoIcon = styled.div`
  width: 64px;
  height: 64px;
  background: ${props => props.theme.gradients.primary};
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin: 0 auto ${props => props.theme.spacing.lg};
  box-shadow: ${props => props.theme.shadows.glow};
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: ${props => props.theme.typography.weights.bold};
  color: ${props => props.theme.colors.textLight};
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.subtextGray};
  margin: 0 0 ${props => props.theme.spacing.xl};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const InputGroup = styled.div`
  text-align: left;
`;

const Label = styled.label`
  display: block;
  font-size: 0.8125rem;
  font-weight: ${props => props.theme.typography.weights.medium};
  color: ${props => props.theme.colors.textDark};
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  border-radius: 12px;
  font-size: 0.9375rem;
  color: ${props => props.theme.colors.textLight};
  transition: all 0.2s;

  &::placeholder {
    color: ${props => props.theme.colors.subtextGray};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accentPurple};
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.15);
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

const Button = styled(motion.button)`
  width: 100%;
  padding: 14px;
  background: ${props => props.theme.gradients.primary};
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: ${props => props.theme.typography.weights.semibold};
  font-size: 0.9375rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: ${props => props.theme.shadows.glow};
  margin-top: ${props => props.theme.spacing.sm};

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled(motion.div)`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #EF4444;
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 0.875rem;
  margin-top: ${props => props.theme.spacing.md};
`;

const FooterText = styled.p`
  font-size: 0.75rem;
  color: ${props => props.theme.colors.subtextGray};
  margin-top: ${props => props.theme.spacing.lg};
`;

const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      onLogin(data.session);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginWrapper>
      <LoginCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <LogoIcon>✨</LogoIcon>
        <Title>Welcome Back</Title>
        <Subtitle>Sign in to access the Neal admin panel</Subtitle>

        <Form onSubmit={handleLogin}>
          <InputGroup>
            <Label>Email</Label>
            <Input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </InputGroup>

          <InputGroup>
            <Label>Password</Label>
            <Input
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </InputGroup>

          <Button
            type="submit"
            disabled={loading}
            whileTap={{ scale: 0.98 }}
          >
            {loading ? (
              <>
                <Spinner />
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </Button>
        </Form>

        {error && (
          <ErrorMessage
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {error}
          </ErrorMessage>
        )}

        <FooterText>
          Powered by Continua Labs
        </FooterText>
      </LoginCard>
    </LoginWrapper>
  );
};

export default AdminLogin;
